import { MepInsightsService } from "../../../../../../../../services/mep/mep-insights.service";
import { MepInsightsRequest, MepInsightsEmissionByMonth } from "../../../../../../../../models/mep/mep-insights.models";
import * as Enumerable from "linq";
import { ConditionalDecimalPlacesValueConverter } from "../../../../converters/conditional-decimal-places-value-converter";
import { LineChartHelper } from "../../../../common-vm/line-chart-helper";
import { ChartLegendViewModel } from "../../../../common-vm/chart-legend-view-model";
import { MepEventMessages, MepTabsEnum } from "../../../../../../../../models/mep/mep.models";
import { EventAggregator } from "aurelia-event-aggregator";
import { ConfigService } from "@rightship-tech/common-ui/services/basic/config.service";
import { useView } from "aurelia-framework";
const am4core = await import("@amcharts/amcharts4/core");
const am4charts = await import("@amcharts/amcharts4/charts");
const am4themes_animated = (await import("@amcharts/amcharts4/themes/animated")).default;

@useView('views/pages/modules/mep/tabs/mep-insights/partials/mep-insights-emissions-by-month/mep-insights-emissions-by-month.html')
export class MepInsightsEmissionsByMonth {
    isEmissionsByMonthLoading: boolean;
    emissionsByMonth: MepInsightsEmissionByMonth[];
    emissionsByMonthChart: MepInsightsEmissionsByMonthChartViewModel;
    isMepTrial: boolean = false;

    constructor(private mepInsightsService: MepInsightsService, private eventAggregator: EventAggregator, isMepTrial: boolean, private configService: ConfigService) {
        this.isMepTrial = isMepTrial;
    }

    getInsightsEmissionsByMonth(request: MepInsightsRequest) {
        return new Promise<string>((resolve, reject) => {
            this.isEmissionsByMonthLoading = true;
            this.mepInsightsService.getMepInsightsEmissionsByMonth(request).then(response => {
                this.emissionsByMonth = response.results;
                if (this.emissionsByMonth && this.emissionsByMonth.length > 0) {
                    this.emissionsByMonthChart = new MepInsightsEmissionsByMonthChartViewModel(this.emissionsByMonth, this.configService);
                }
                this.isEmissionsByMonthLoading = false;
                resolve(null);
            }).catch(() => {
                this.isEmissionsByMonthLoading = false;
                reject("MEP Insights Emissions by Month - Error retrieving the emissions by month!");
            });
        });
    }

    clearEmissionsByMonth() {
        this.emissionsByMonth = null;
        this.emissionsByMonthChart = null;
    }

    navigateToEmissionTargets() {
        this.eventAggregator.publish(MepEventMessages.ChangeMepTab, {
            step: MepTabsEnum.EmissionTargets
        });
    }
}

export class MepInsightsEmissionsByMonthChartViewModel {

    legend: ChartLegendViewModel;
    scientificNotationConverter: ConditionalDecimalPlacesValueConverter = new ConditionalDecimalPlacesValueConverter();

    constructor(private items: MepInsightsEmissionByMonth[], private configService: ConfigService) {
        this.initChart();
    }

    initChart(chartDivName: string = "chartdiv") {
        if (!this.items)
            return;

        const actualName = "Actual";
        const targetName = "Target";
        // here we re-arrange the data to a model that looks like the below 

        /*
        [
            {
                month:'jan'
                Actual: 21213,
                Target: 21234
            }
        ]
        */

        const unifiedEmissionsData = [];

        this.items.forEach(item => {
            unifiedEmissionsData.push({
                monthNo: item.monthNo,
                monthName: item.monthName,
                value: item.actualEmissionsByMonthInTonnes,
                name: actualName,
                id: 1,
                calendarYear: item.calendarYear
            });
            unifiedEmissionsData.push({
                monthNo: item.monthNo,
                monthName: item.monthName,
                value: item.targetEmissionsByMonthInTonnes,
                name: targetName,
                id: 2,
                calendarYear: item.calendarYear
            });
        });

        const dataForChart = Enumerable.from(unifiedEmissionsData).groupBy(x => x.monthNo)
            .select(x => {
                const item: any = {};
                item.month = x.first().monthName.substring(0, 3);
                item.calendarYear = x.first().calendarYear;
                x.forEach(y => {
                    if (y.name == actualName) {
                        item[actualName] = this.scientificNotationConverter.toView(y.value, null);
                    }
                    else {
                        item[targetName] = this.scientificNotationConverter.toView(y.value, null);
                    }
                });
                return item;
            }).toArray();

        //Create chart instance 
        am4core.useTheme(am4themes_animated);
        am4core.addLicense(this.configService.config.settings.amChartsLicense);
        const chart = am4core.create(chartDivName, am4charts.XYChart);

        // Create category axes that will use the month property we created in dataForChart
        LineChartHelper.AddXCategoryAxis(chart, "month");
        const yAxis = LineChartHelper.AddYValueAxis(chart, 0);
        yAxis.extraMin = 0;

        const dataItems = Enumerable.from(unifiedEmissionsData).groupBy(x => x.name);
        const legendData = dataItems.select(x => {
            return {
                id: x.first().id,
                name: `${x.key()}`
            }
        }).distinct(x => x.id).toArray();

        this.legend = new ChartLegendViewModel();
        this.legend.generateLegendItems(legendData);

        //We want to draw a line Actual and Target and the dots would then be the months thus we create a line series for each one
        dataItems.forEach(item => {
            const color = this.legend.getColor(item.first().id);
            LineChartHelper.AddCategorySeries(chart, color, "month", item.key(), `{categoryX} {calendarYear} : {${item.key()}}`);
        });

        chart.numberFormatter.numberFormat = "#";

        // chart.tooltip.background.fill = am4core.color("#F0F");

        //use XY cursor to highlight all points 
        chart.cursor = new am4charts.XYCursor();

        chart.data = dataForChart;
    }

    getLegendTooltipText(legendName) {

        if (legendName == "Target") {
            return "Target is based on emission target defined for the reporting period in Emissions <a class='link' click.delegate='navigateToEmissionTargets()'>Emissions Targets page</a>";
        }
    }
}